import { api } from "@/lib/api";

// The webhook settings pages, read through the Go API (ADR-0007 slice F).
//
// These were the portal's last raw collection readers outside the report, and
// the index page was also its worst request pattern: one list call for the
// webhooks, then three more per webhook — last delivery, 24h successes, 24h
// failures — in a Promise.all. Ten webhooks meant thirty-one requests to draw
// one table. `fetchWebhooks` is one.
//
// The customer scope is gone from the client entirely. Every call derived it
// from the caller's own customer_users row and passed it back down, so the
// server works it out for itself and there is no id to pass — or to guess.
export namespace WebhookApi {
  export type Access = {
    /** Whether to show the webhook settings at all. */
    has_access: boolean;
    /** Whether to show them writable: customer admins only. */
    can_manage: boolean;
    customer_id: string;
    customer_name: string;
  };

  export type DeliveryStats = { success: number; failed: number };

  export type WebhookRow<TWebhook, TLog> = {
    record: TWebhook;
    /** The newest delivery attempt, or null when there has never been one. */
    last_delivery: TLog | null;
    stats_24h: DeliveryStats;
  };

  /** Whether the signed-in user may see the webhook pages, and edit them. */
  export async function fetchAccess(signal?: AbortSignal): Promise<Access> {
    return api<Access>("/app/me/webhook-access", { method: "GET", signal });
  }

  /**
   * The caller's customer's live webhooks, newest first, each with the
   * delivery summary the table renders. Soft-deleted rows are left out.
   */
  export async function fetchWebhooks<TWebhook, TLog>(signal?: AbortSignal): Promise<WebhookRow<TWebhook, TLog>[]> {
    const res = await api<{ webhooks?: WebhookRow<TWebhook, TLog>[] }>("/app/webhooks", {
      method: "GET",
      signal,
    });
    return res.webhooks ?? [];
  }

  /**
   * One webhook, or undefined when it does not exist or belongs to another
   * company — the detail page shows the same "kunne ikke hente" either way.
   */
  export async function fetchWebhook<TWebhook>(webhookId: string, signal?: AbortSignal): Promise<TWebhook | undefined> {
    if (!webhookId) return undefined;
    const res = await api<{ webhook: TWebhook | null }>("/app/webhooks/one", {
      method: "GET",
      query: { webhook_id: webhookId },
      signal,
    });
    return res.webhook ?? undefined;
  }

  /** One page of a webhook's delivery log, newest attempt first. */
  export async function fetchLogs<TLog>(
    webhookId: string,
    page = 1,
    perPage = 50,
    signal?: AbortSignal,
  ): Promise<{ items: TLog[]; totalItems: number }> {
    if (!webhookId) return { items: [], totalItems: 0 };
    const res = await api<{ items?: TLog[]; total_items?: number }>("/app/webhook-logs", {
      method: "GET",
      query: { webhook_id: webhookId, page, per_page: perPage },
      signal,
    });
    return { items: res.items ?? [], totalItems: res.total_items ?? 0 };
  }
}
